import { MessageCircle, Twitter, Facebook, Linkedin, Link2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { toast } from 'sonner';

interface SocialShareProps {
  url: string;
  title: string;
  description?: string;
  className?: string;
}

const SocialShare = ({ url, title, description = '', className = '' }: SocialShareProps) => {
  const [copied, setCopied] = useState(false);
  
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description);

  const shareLinks = [
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      href: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
      color: 'bg-[hsl(142_70%_45%)] hover:bg-[hsl(142_70%_40%)]',
    },
    {
      name: 'Twitter',
      icon: Twitter,
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      color: 'bg-[hsl(203_89%_53%)] hover:bg-[hsl(203_89%_48%)]',
    },
    {
      name: 'Facebook',
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      color: 'bg-[hsl(220_46%_48%)] hover:bg-[hsl(220_46%_43%)]',
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      color: 'bg-[hsl(201_100%_35%)] hover:bg-[hsl(201_100%_30%)]',
    },
  ];

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      <span className="text-sm font-medium text-muted-foreground">Share this article</span>
      <div className="flex flex-wrap gap-2">
        {shareLinks.map((link) => (
          <Button
            key={link.name}
            variant="ghost"
            size="icon"
            className={`${link.color} text-white rounded-full w-10 h-10 transition-transform hover:scale-110`}
            asChild
          >
            <a
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Share on ${link.name}`}
            >
              <link.icon className="w-4 h-4" />
            </a>
          </Button>
        ))}
        <Button
          variant="ghost"
          size="icon"
          onClick={copyToClipboard}
          className="bg-secondary hover:bg-secondary/80 text-foreground rounded-full w-10 h-10 transition-transform hover:scale-110"
          aria-label="Copy link"
        >
          {copied ? <Check className="w-4 h-4 text-[hsl(142_70%_45%)]" /> : <Link2 className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
};

export default SocialShare;
